///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

//-----------------------------------------------------------------------------------------
//* \brief
//? Per route <title>, description and canonical URL.
//?
//? public/index.html can only carry ONE canonical tag, and it applied to every route of
//? the SPA - every page was telling search engines it was a duplicate of the home page.
//? Rendered once inside the Router, this component derives the canonical from the current
//? path instead, so dynamic routes (/docs/:slug) get a correct one without extra wiring.
//?
//? A page that needs its own title just renders its own <Helmet> further down the tree:
//? react-helmet-async lets the deeper (later mounted) instance win for the same tag.
//-----------------------------------------------------------------------------------------

import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

export const SITE_URL = 'https://programintrastat.ro';

//-----------------------------------------------------------------------------------------
//* \brief
//? Titles and descriptions for the static public routes. Routes missing from here keep
//? the defaults from index.html and still get a correct canonical.
//-----------------------------------------------------------------------------------------
const ROUTE_META = {
    '/': {
        title: 'Program Generare Intrastat 2026 Gratuit | Din Excel în XML | Coduri NC',
        description:
            'Program intrastat 2026 online. Generează declarația Intrastat din Excel în XML, ' +
            'cu validarea codurilor NC8 și a datelor înainte de depunere. 3 luni gratuit.',
    },
    '/verificare-xml-intrastat': {
        title: 'Verificare fișier XML Intrastat — validator gratuit online',
        description:
            'Verificați gratuit fișierul XML Intrastat înainte de depunere: coduri NC8, ' +
            'valori obligatorii, țări, natura tranzacției. Aflați de ce aplicația oficială ' +
            'INTRASTAT respinge declarația. Fără cont, fișierul nu se încarcă pe server.',
    },
    '/docs': {
        title: 'Documentație — Program Intrastat',
        description:
            'Ghid pas cu pas pentru generarea declarației Intrastat: machete, firme, ' +
            'încărcarea fișierului Excel și generarea fișierului XML.',
    },
    '/info': {
        title: 'Informații despre Intrastat — Program Intrastat',
        description: 'Ce este declarația Intrastat, cine are obligația de a o depune și cum se completează.',
    },
    '/contact': {
        title: 'Contact — Program Intrastat',
        description: 'Contactați echipa Program Intrastat pentru întrebări despre declarația Intrastat.',
    },
    '/sign-in': { title: 'Autentificare — Program Intrastat' },
    '/register': { title: 'Creare cont — Program Intrastat' },
    '/termeni-si-conditii': { title: 'Termeni și condiții — Program Intrastat' },
    '/politica-de-confidentialitate': { title: 'Politica de confidențialitate — Program Intrastat' },
    '/politica-livrare-anulare': {
        title: 'Politica de livrare și de anulare a comenzii — Program Intrastat',
        description:
            'Cum se livrează abonamentul după plată, cum se anulează o comandă și în ce ' +
            'condiții se restituie sumele achitate.',
    },
};

//-----------------------------------------------------------------------------------------
//* \brief
//? Routes that must never be indexed. The SPA answers every URL with 200, so without this
//? the not found page would be indexed as a real (soft 404) page.
//-----------------------------------------------------------------------------------------
const KNOWN_PATHS = new Set([
    ...Object.keys(ROUTE_META),
    '/IntHelpers',
]);

const isIndexable = (path) => KNOWN_PATHS.has(path) || path.startsWith('/docs/');

//-----------------------------------------------------------------------------------------
//* \brief
//? Removes a trailing slash so /docs and /docs/ never compete as two separate URLs.
//-----------------------------------------------------------------------------------------
export const canonicalFor = (pathname) => {
    const path = pathname !== '/' && pathname.endsWith('/')
        ? pathname.slice(0, -1)
        : pathname;
    return `${SITE_URL}${path === '/' ? '' : path}`;
};

const Seo = () => {
    const { pathname } = useLocation();
    const meta = ROUTE_META[pathname] || {};
    const canonical = canonicalFor(pathname);

    return (
        <Helmet>
            {meta.title && <title>{meta.title}</title>}
            {meta.description && <meta name="description" content={meta.description} />}
            <link rel="canonical" href={canonical} />
            <meta property="og:url" content={canonical} />
            {!isIndexable(pathname) && <meta name="robots" content="noindex, follow" />}
        </Helmet>
    );
};

export default Seo;
